// src/components/admin/AdvertisementManagement.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const AdvertisementManagement = () => {
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [currentAd, setCurrentAd] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    company: '',
    link: '',
    backgroundColor: '#6B8E23',
    textColor: '#FFFFFF',
    is_active: true,
    display_order: 0
  });

  useEffect(() => {
    fetchAdvertisements();
  }, []);

  const fetchAdvertisements = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }

      const response = await fetch(`${API_BASE}/admin/advertisements`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setAdvertisements(data.data || []);
      } else {
        toast.error(data.message || 'Failed to fetch advertisements');
      }
    } catch (err) {
      console.error('Error fetching advertisements:', err);
      toast.error('Failed to fetch advertisements');
    } finally {
      if (loading) setLoading(false);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setCurrentAd(null);
    setImageFile(null);
    setImagePreview(null);
    setFormData({
      title: '',
      description: '',
      company: '',
      link: '',
      backgroundColor: '#6B8E23',
      textColor: '#FFFFFF',
      is_active: true,
      display_order: 0
    });
    setShowModal(true);
  };

  const handleEdit = (ad) => {
    setModalMode('edit');
    setCurrentAd(ad);
    setImageFile(null);
    setImagePreview(ad.image_url || null);
    setFormData({
      title: ad.title || '',
      description: ad.description || '',
      company: ad.company || '',
      link: ad.link || '',
      backgroundColor: ad.background_color || '#6B8E23',
      textColor: ad.text_color || '#FFFFFF',
      is_active: ad.is_active !== undefined ? ad.is_active : true,
      display_order: ad.display_order || 0
    });
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return null;

    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('image', imageFile);

      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/admin-upload/advertisements/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        return data.data.url;
      } else {
        toast.error(data.message || 'Failed to upload image');
        return null;
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      toast.error('Failed to upload image');
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let imagePath = currentAd?.image_url || '';

      if (imageFile) {
        imagePath = await uploadImage();
        if (!imagePath && imageFile) {
          return;
        }
      }

      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const url = modalMode === 'create'
        ? `${API_BASE}/admin/advertisements`
        : `${API_BASE}/admin/advertisements/${currentAd.id}`;

      const method = modalMode === 'create' ? 'POST' : 'PUT';

      const submitData = {
        title: formData.title,
        description: formData.description,
        company: formData.company,
        link: formData.link,
        background_color: formData.backgroundColor,
        text_color: formData.textColor,
        is_active: formData.is_active,
        display_order: formData.display_order,
        image_url: imagePath
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (data.success) {
        setShowModal(false);
        fetchAdvertisements();
        toast.success(`Advertisement ${modalMode === 'create' ? 'created' : 'updated'} successfully`);
      } else {
        toast.error(data.message || `Failed to ${modalMode === 'create' ? 'create' : 'update'} advertisement`);
      }
    } catch (err) {
      console.error(`Error ${modalMode === 'create' ? 'creating' : 'updating'} advertisement:`, err);
      toast.error(`Failed to ${modalMode === 'create' ? 'create' : 'update'} advertisement`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this advertisement?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/admin/advertisements/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        fetchAdvertisements();
        toast.success('Advertisement deleted successfully');
      } else {
        toast.error(data.message || 'Failed to delete advertisement');
      }
    } catch (err) {
      console.error('Error deleting advertisement:', err);
      toast.error('Failed to delete advertisement');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" style={{ color: '#6B8E23' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0" style={{ color: '#6B8E23' }}>Advertisement Management</h2>
        <button
          className="btn btn-sm"
          style={{ backgroundColor: '#6B8E23', color: 'white' }}
          onClick={handleCreate}
        >
          <i className="bi bi-plus-lg me-2"></i>Add New Advertisement
        </button>
      </div>

      {/* Advertisements Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Preview</th>
                  <th>Title</th>
                  <th>Company</th>
                  <th>Link</th>
                  <th>Status</th>
                  <th>Order</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {advertisements.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">
                      No advertisements found. Click "Add New Advertisement" to create one.
                    </td>
                  </tr>
                ) : (
                  advertisements.map((ad) => (
                    <tr key={ad.id}>
                      <td>
                        <div style={{
                          width: '80px',
                          height: '50px',
                          backgroundColor: ad.background_color || '#6B8E23',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: ad.text_color || '#FFFFFF',
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}>
                          {ad.company}
                        </div>
                      </td>
                      <td className="fw-bold">{ad.title}</td>
                      <td>{ad.company}</td>
                      <td>
                        <a href={ad.link} target="_blank" rel="noopener noreferrer" className="text-decoration-none small">
                          {ad.link?.substring(0, 30)}...
                        </a>
                      </td>
                      <td>
                        <span className={`badge ${ad.is_active ? 'bg-success' : 'bg-secondary'}`}>
                          {ad.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{ad.display_order}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => handleEdit(ad)}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(ad.id)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                <h5 className="modal-title">
                  {modalMode === 'create' ? 'Create New Advertisement' : 'Edit Advertisement'}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Title *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Company *</label>
                      <input
                        type="text"
                        className="form-control"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description *</label>
                    <textarea
                      className="form-control"
                      name="description"
                      rows="3"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                    ></textarea>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Link URL *</label>
                    <input
                      type="url"
                      className="form-control"
                      name="link"
                      value={formData.link}
                      onChange={handleInputChange}
                      placeholder="https://example.com"
                      required
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Background Color</label>
                      <input
                        type="color"
                        className="form-control form-control-color"
                        name="backgroundColor"
                        value={formData.backgroundColor}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Text Color</label>
                      <input
                        type="color"
                        className="form-control form-control-color"
                        name="textColor"
                        value={formData.textColor}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Display Order</label>
                      <input
                        type="number"
                        className="form-control"
                        name="display_order"
                        value={formData.display_order}
                        onChange={handleInputChange}
                        min="0"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label d-block">Status</label>
                      <div className="form-check form-switch mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label">
                          {formData.is_active ? 'Active' : 'Inactive'}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Advertisement Image (Optional)</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    {imagePreview && (
                      <div className="mt-3">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Preview */}
                  <div className="mb-3">
                    <label className="form-label">Preview</label>
                    <div style={{
                      backgroundColor: formData.backgroundColor,
                      color: formData.textColor,
                      padding: '2rem',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '0.875rem', opacity: 0.8, marginBottom: '0.5rem' }}>
                        {formData.company || 'Company Name'}
                      </div>
                      <h4 style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                        {formData.title || 'Advertisement Title'}
                      </h4>
                      <p style={{ marginBottom: 0 }}>
                        {formData.description || 'Description goes here...'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                    {modalMode === 'create' ? 'Create' : 'Update'} Advertisement
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvertisementManagement;
